import { FC, useEffect } from 'react'
import { useEffectOnce } from 'react-use'

import { Dashboard } from '../../containers'
import useCheckPermission from '../../helpers/hooks/useCheckPermission'
import { setSelectedDays, setSelectedMobileDay } from '../../redux/@interface/calendar/calendarReducer'
import {
    fetchIncompleteUsers,
    fetchNotifications,
    fetchOpenAbsenceHours,
    fetchOpenLeaveOfAbsenceHours,
    initializeIncompleteUsers,
    initializeOpenAbsenceHours,
    initializeOpenLeaveOfAbsenceHours,
} from '../../redux/@interface/notifications/notificationsActions'
import { getNotificationsInitializedState } from '../../redux/@interface/notifications/notificationsReducer'
import { fetchDashboardSettings } from '../../redux/dashboard/dashboardActions'
import { getPacoPayrollPeriods } from '../../redux/payrollPeriods/payrollPeriodsActions'
import { useTypedDispatch, useTypedSelector } from '../../redux/store'
import { DashboardSettingType } from '../../types'
import { setCustomDateRegisteredShift } from './helpers'

const ConnectedDashboard: FC = () => {
    const dispatch = useTypedDispatch()

    const { isLoading: isLoadingShifts } = useTypedSelector(state => state.shiftsCalendarReducer)
    const { user, isLoading: isLoadingUser } = useTypedSelector(state => state.authenticatedUserReducer)
    const {
        notifications,
        isLoading: isLoadingNotifications,
        isOpenLeaveOfAbsenceHoursInitialized,
        isOpenAbsenceHoursInitialized,
    } = useTypedSelector(state => state.notificationsReducer)
    const notificationsIsInitialized = useTypedSelector(getNotificationsInitializedState)
    const { payrollPeriods } = useTypedSelector(state => state.pacoPayrollPeriodsReducer)

    const userWidgets: DashboardSettingType[] = user ? user.dashboardSettings.map(dashboardSetting => dashboardSetting.type) : []
    const permissions = user?.permissions || []
    const isInitialized = notificationsIsInitialized && isOpenLeaveOfAbsenceHoursInitialized && isOpenAbsenceHoursInitialized

    const canViewAbsenceHours = useCheckPermission('view-all-absence-hours')
    const canViewLeaveOfAbsenceHours = useCheckPermission('view-all-leave-of-absence-hours')
    const canViewUserProfiles = useCheckPermission('view-all-user-profiles')
    const canViewEmployments = useCheckPermission('view-all-employments')

    const { date } = notifications.firstUpcomingRegisteredShiftStartTimeForFourWeeks || {}
    const dateFromApiRunningTracks = date ? new Date(date) : new Date()

    const handleWidgetClick = () => {
        const weekDays = setCustomDateRegisteredShift(dateFromApiRunningTracks)

        dispatch(setSelectedDays(weekDays))
        dispatch(setSelectedMobileDay(weekDays[0]))
    }

    useEffectOnce(() => {
        // If notificationsIsInitialized is false then the store is still initializing and fetching notifications data
        // (see getGeneralData in appSaga.js).
        if (notificationsIsInitialized) {
            dispatch(fetchNotifications())
        }
    })

    useEffect(() => {
        if (notificationsIsInitialized && !! payrollPeriods.length) {
            if (canViewAbsenceHours) {
                dispatch(fetchOpenAbsenceHours())
            } else {
                dispatch(initializeOpenAbsenceHours())
            }

            if (canViewLeaveOfAbsenceHours) {
                dispatch(fetchOpenLeaveOfAbsenceHours())
            } else {
                dispatch(initializeOpenLeaveOfAbsenceHours())
            }

            if (canViewEmployments && canViewUserProfiles) {
                dispatch(fetchIncompleteUsers())
            } else {
                dispatch(initializeIncompleteUsers())
            }
        }
    }, [notificationsIsInitialized, payrollPeriods])

    useEffect(() => {
        dispatch(getPacoPayrollPeriods())
        dispatch(fetchDashboardSettings())
    }, [])

    return (
        <Dashboard
            isLoadingShifts={isLoadingShifts}
            isLoadingUser={isLoadingUser || ! user}
            isLoadingNotifications={isLoadingNotifications || ! isInitialized}
            notifications={notifications}
            payrollPeriods={payrollPeriods}
            permissions={permissions}
            user={user}
            userWidgetTypes={userWidgets}
            handleWidgetClick={handleWidgetClick}
        />
    )
}

export default ConnectedDashboard
